import { transformBenefitsList } from "@/app/(site)/_utils/transformData/transformBenfitList"
import { transformTechnicalDetails } from "@/app/(site)/_utils/transformData/transformTechnicalDetails"
import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { ScriptLdJson } from "@/lib/scripts/ScriptLdJson"
import { Page } from "@/types"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import placeholder from "@assets/images/placeholder.png"
import { Product } from "schema-dts"

export interface SizeInfo {
    sizeId: string
    name: string
    item: string
    sku: string
    isAvailable: boolean
    isSelected: boolean
}

/**
 * Extracts size information from products fetched from the CheckoutAPIProduct or RelatedProduct.
 * The function iterates through the product items, maps them to a simplified object containing
 * sizeId, name, item, sku, and availability based on the stock status, and returns a sorted array
 * of these objects by sizeId in ascending order.
 *
 * @param product The product to extract size information from. Can be either CheckoutAPIProduct or RelatedProduct.
 * @returns {SizeInfo[]} An array of objects containing the size information. If the product or its items are not defined, returns an empty array.
 */
export const extractSizeInfo = (
    product: CheckoutAPIProduct | RelatedProduct | ProductData
): SizeInfo[] => {
    if (!product || !product.items || product.items.length === 0) {
        // @TODO: only need to pass the items here not the entire product...
        return []
    }

    return product.items
        .map(item => ({
            sizeId: item.sizeId,
            name: item.name,
            item: item.item,
            sku: item.sku,
            isAvailable: item.stock === "yes",
            isSelected: false
        }))
        .sort((a, b) => parseInt(a.sizeId) - parseInt(b.sizeId))
}

/**
 * Extracts detailed information from a single product and an array of related products. This includes various
 * details such as media, technical details, benefits, pricing, availability, and more.
 *
 * @param {(CheckoutAPIProduct | RelatedProduct)} product The main product object from which to extract information.
 * @param {(CheckoutAPIProduct | RelatedProduct)[]} allProducts An array of all products (including the main product)
 *        to extract unique fit types across products.
 * @param {Page | undefined} pageData Optional. Page data that may contain additional media or information related to the product.
 *
 * @returns An object containing the extracted product details.
 * The function assumes the existence of `transformTechnicalDetails` and `transformBenefitsList` functions for processing
 * technical details and product benefits, respectively.
 */

export const extractProductDetails = (
    product: CheckoutAPIProduct | RelatedProduct,
    allProducts: (CheckoutAPIProduct | RelatedProduct)[],
    pageData: Page | undefined
) => {
    const lifestyleImages = product?.media?.standard?.slice(4)

    // Assuming transformTechnicalDetails is a function you have defined elsewhere
    const technicalDetails = transformTechnicalDetails([
        product?.technical_details_detail1,
        product?.technical_details_detail2,
        product?.technical_details_detail3,
        product?.technical_details_detail4,
        product?.technical_details_detail5,
        product?.technical_details_detail6,
        product?.technical_details_detail7,
        product?.technical_details_detail8,
        product?.technical_details_detail9,
        product?.technical_details_detail10,
        product?.technical_details_detail11,
        product?.technical_details_detail12
    ])

    // Assuming transformBenefitsList is a function you have defined elsewhere
    const productBenefits = transformBenefitsList(product?.product_benefits_list ?? "")

    const badges = product?.badge || []
    const productPrice = product?.price || ""
    const productName = product?.name || ""
    const isProductAvailability = product?.available || false
    const selectedFitType = product?.fit_type_fit_type

    const fit_type_fit_type = Array.from(
        new Set(
            allProducts.reduce<string[]>((acc, prod) => {
                if (prod.fit_type_fit_type) {
                    acc.push(prod.fit_type_fit_type)
                }
                return acc
            }, [])
        )
    ).sort()

    const descriptionHtml = product?.descriptionHtml || ""
    const technicalDetailImages = pageData?.technicalDetailImages || []
    const showAsOnSale = product?.showAsOnSale || false
    const showAsNew = product?.showAsNew || false

    const productExcerpt = product?.excerpt || ""

    return {
        lifestyleImages,
        technicalDetails,
        productBenefits,
        badges,
        productPrice,
        productName,
        isProductAvailability,
        selectedFitType,
        fit_type_fit_type,
        descriptionHtml,
        technicalDetailImages,
        showAsOnSale,
        showAsNew,
        productExcerpt
    }
}

/**
 * Generates the JSON-LD schema for a product.
 *
 * @param {ProductData} product - The product object.
 * @return {JSX.Element} A script element with the JSON-LD data.
 */
export const generateProductLdJson = (
    product: ProductData,
    params: { slug: string; locale: string }
): JSX.Element => {
    const siteUrl = process.env.NEXT_PUBLIC_SITEMAP_DOMAIN || "https://localhost:3000"
    const id = "product-ld-json"

    // Ugly fix to get the currency from product?.price string
    // TODO: Look into how to get it from the language selector or from centra
    const priceCurrency = product?.price || ""
    const currency = priceCurrency.match(/[A-Z]{3}/)?.[0] || ""

    return ScriptLdJson<Product>(
        {
            "@context": "https://schema.org",
            "@type": "Product",
            name: product?.name || "",
            image: product?.media?.small?.[0].url ?? placeholder,
            description: product?.descriptionHtml || "",
            sku: product?.sku || "",
            brand: {
                "@type": "Brand",
                name: product?.brandName || ""
            },
            color: product?.color_swatch?.color_name || "",
            offers: product?.items.map(item => {
                return {
                    "@type": "Offer",
                    price: product?.priceAsNumber ?? 0,
                    priceCurrency: currency,
                    availability:
                        item.stock === "yes"
                            ? "https://schema.org/InStock"
                            : "https://schema.org/OutOfStock",
                    sku: item.sku,
                    url: `${siteUrl}/${params.locale}/product/${product?.uri ?? ""}`
                }
            }),
            category: product?.categoryName || ""
        },
        id
    )
}
